import React from 'react';
import { FiBriefcase, FiCheckCircle } from 'react-icons/fi';
import { getItemHierarchy } from '../components/itemsFamiliesUi';
import { managedByAdmin } from '../utils/tenderManagement';
import { buildDeliveryNoteUnitOptions, unitHierarchyLabel } from '../utils/deliveryNoteUnits';

const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
const hierarchyLabels = { family: 'Family', subfamily: 'Subfamily', category: 'Category', subcategory: 'Subcategory' };
const suppliers = (item) => item.item_suppliers?.length
  ? item.item_suppliers.map((link) => ({ name: link.supplier?.name, code: link.supplier_code }))
  : (item.suppliers || []).map((supplier) => ({ name: supplier.name, code: supplier.supplier_code }));

export function equivalentItemMatches(item, search, t = (value) => value) {
  const text = normalize([item.name, item.code, item.supplier_code,
    ...suppliers(item).flatMap((supplier) => [supplier.name, supplier.code]),
    ...Object.values(getItemHierarchy(item)),
    ...[item.unit, item.subunit, item.subunit2].flatMap((unit) => [unit?.name, unit?.code]), item.unit_name,
    managedByAdmin(item) ? t('Corporate') : '', item.is_tendered ? t('Tendered') : '',
  ].join(' '));
  return normalize(search).trim().split(/\s+/).every((term) => text.includes(term));
}

export function equivalentMemberItem(member, choices = []) {
  return member.item || choices.find((item) => Number(item.id) === Number(member.item_id)) || {
    id: member.item_id, name: member.item_name, code: member.item_code, unit_name: member.item_unit_name,
  };
}

export function EquivalentItemIdentity({ item, t }) {
  return <span className="eq-itemIdentity">
    <strong>{item.name}</strong><span className="eq-itemMeta">{item.code || '—'}</span>
    {(managedByAdmin(item) || item.is_tendered) && <span className="eq-itemFlags">
      {managedByAdmin(item) && <span className="eq-itemFlag"><FiBriefcase aria-hidden="true" />{t('Corporate')}</span>}
      {item.is_tendered && <span className="eq-itemFlag eq-itemFlag--tendered"><FiCheckCircle aria-hidden="true" />{t('Tendered')}</span>}
    </span>}
  </span>;
}

export function EquivalentItemSuppliers({ item, t }) {
  const links = suppliers(item).filter((supplier) => supplier.name || supplier.code);
  return <span className="eq-itemSuppliers">
    {links.length ? links.map((supplier, index) => <span key={`${supplier.name}:${supplier.code}:${index}`}>
      <span>{supplier.name || '—'}</span>{supplier.code && <small>{t('Supplier reference')}: {supplier.code}</small>}
    </span>) : <span>—</span>}
    {item.supplier_code && !links.some((supplier) => supplier.code === item.supplier_code) && <small>{t('Supplier reference')}: {item.supplier_code}</small>}
  </span>;
}

export function EquivalentItemClassification({ item, t }) {
  const hierarchy = getItemHierarchy(item);
  return <span className="eq-itemClassification">{Object.entries(hierarchyLabels).map(([key, label]) =>
    <span key={key}><small>{t(label)}</small><span>{hierarchy[key] === '-' ? '—' : hierarchy[key]}</span></span>
  )}</span>;
}

export function EquivalentItemUnits({ item, languageId }) {
  const options = buildDeliveryNoteUnitOptions(item, undefined, languageId);
  return <span className="eq-itemUnits"><span>{options[0]?.label || item.unit_name || '—'}</span>
    {options.length > 1 && <small>{unitHierarchyLabel(item, undefined, languageId)}</small>}
  </span>;
}

export default function EquivalentItemDetails({ item, languageId, t }) {
  return <span className="eq-itemDetails">
    <EquivalentItemIdentity item={item} t={t} />
    <span className="eq-itemFacts"><span><small>{t('Suppliers')}</small><EquivalentItemSuppliers item={item} t={t} /></span>
      <span><small>{t('Inventory unit')}</small><EquivalentItemUnits item={item} languageId={languageId} /></span></span>
    <EquivalentItemClassification item={item} t={t} />
  </span>;
}
